import ExcelJS from 'exceljs';

export interface ExcelData {
  headers: string[];
  rows: string[][];
}

/**
 * Read Excel file and convert to structured data
 */
export const readExcelFile = async (file: File): Promise<ExcelData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async (event) => {
      try {
        const buffer = event.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        
        // Get the first worksheet
        const worksheet = workbook.getWorksheet(1);
        if (!worksheet) {
          throw new Error('No worksheet found in the Excel file');
        }

        const headers: string[] = [];
        const rows: string[][] = [];
        
        let headerRowFound = false;
        worksheet.eachRow((row, rowNumber) => {
          if (headerRowFound) {
            const rowValues: string[] = [];
            row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
              rowValues[colNumber - 1] = getCellValue(cell);
            });
            if (rowValues.some(val => val !== '')) {
              rows.push(rowValues);
            }
            return;
          }

          const rowValues: string[] = [];
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            rowValues[colNumber - 1] = getCellValue(cell);
          });

          // A row is likely a header if it has multiple non-empty values
          if (rowValues.filter(v => v !== '').length >= 2) {
            headers.push(...rowValues);
            headerRowFound = true;
          }
        });

        resolve({ headers, rows });
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    // Helper to get string value from cell
    const getCellValue = (cell: ExcelJS.Cell): string => {
      let cellValue = '';
      if (cell.value !== null && cell.value !== undefined) {
        if (typeof cell.value === 'object' && 'result' in cell.value) {
          cellValue = String(cell.value.result || '');
        } else if (typeof cell.value === 'object' && 'richText' in cell.value) {
          cellValue = (cell.value as any).richText.map((rt: any) => rt.text).join('');
        } else {
          cellValue = String(cell.value);
        }
      }
      return cellValue.trim();
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read Excel file'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Convert Excel data to CSV-like format for backward compatibility
 */
export const excelDataToCsvFormat = (data: ExcelData): any[] => {
  const { headers, rows } = data;
  
  return rows.map(row => {
    const item: any = {};
    headers.forEach((header, index) => {
      item[header] = row[index] || '';
    });
    return item;
  });
};

/**
 * Normalize header name for flexible matching
 */
const normalizeHeader = (header: string): string => {
  if (!header) return '';
  // Convert to uppercase, remove all non-alphanumeric characters, and trim
  return header.toString().toUpperCase().replace(/[^A-Z0-9]/g, '').trim();
};

/**
 * Create mapping between actual headers and required headers (case-insensitive)
 */
const createHeaderMapping = (
  actualHeaders: string[],
  requiredHeaders: string[]
): { mapping: Record<string, string>; missing: string[] } => {
  const normalizedRequired = requiredHeaders.map(h => ({
    original: h,
    normalized: normalizeHeader(h)
  }));
  
  const normalizedActual = actualHeaders.map(h => ({
    original: h,
    normalized: normalizeHeader(h)
  }));
  
  const mapping: Record<string, string> = {};
  const missing: string[] = [];
  
  for (const req of normalizedRequired) {
    const match = normalizedActual.find(act => act.normalized === req.normalized);
    if (match) {
      mapping[match.original] = req.original;
    } else {
      missing.push(req.original);
    }
  }
  
  return { mapping, missing };
};

/**
 * Validate Excel file has required headers (case-insensitive)
 */
export const validateExcelHeaders = (
  data: ExcelData, 
  requiredHeaders: string[]
): { valid: boolean; missingHeaders: string[]; headerMapping: Record<string, string> } => {
  const { headers } = data;
  const { mapping, missing } = createHeaderMapping(headers, requiredHeaders);
  
  return {
    valid: missing.length === 0,
    missingHeaders: missing,
    headerMapping: mapping
  };
};

/**
 * Convert Excel data to CSV-like format with header mapping for backward compatibility
 */
export const excelDataToCsvFormatWithMapping = (
  data: ExcelData,
  headerMapping: Record<string, string>
): any[] => {
  const { headers, rows } = data;
  
  return rows.map(row => {
    const item: any = {};
    headers.forEach((header, index) => {
      // Use mapped header name if available, otherwise use original
      const mappedHeader = headerMapping[header] || header;
      item[mappedHeader] = row[index] || '';
    });
    return item;
  });
};

/**
 * Process Price List Configuration Excel with automatic percentage conversion
 */
export const processPriceConfigExcel = async (file: File): Promise<any[]> => {
  const data = await readExcelFile(file);
  
  // Expected headers for price configuration
  const expectedHeaders = [
    'Regional Retail AUD Incl',
    'Regional Trade AUD Incl',
    'Metro Retail AUD Incl',
    'Metro Trade AUD Incl',
    'Regional Retail VIP AUD Incl',
    'Regional Trade VIP1 AUD Incl',
    'Regional Trade VIP2 AUD Incl',
    'Metro Retail VIP AUD Incl',
    'Metro Trade VIP1 AUD Incl',
    'Metro Trade VIP2 AUD Incl'
  ];
  
  // Validate headers with flexible matching
  const validation = validateExcelHeaders(data, expectedHeaders);
  if (!validation.valid) {
    throw new Error(`Missing required headers: ${validation.missingHeaders.join(', ')}`);
  }
  
  // Convert to structured format with automatic percentage conversion using header mapping
  const result = data.rows.map(row => {
    const item: Record<string, string> = {};
    data.headers.forEach((header, index) => {
      // Use mapped header name
      const mappedHeader = validation.headerMapping[header] || header;
      let value = row[index] || '';
      
      // Automatic percentage conversion for numeric values
      if (value && !isNaN(Number(value))) {
        const numValue = Number(value);
        // If the value is between 0 and 100, treat as percentage
        // If it's greater than 100, assume it's already in the correct format
        if (numValue > 0 && numValue <= 100) {
          value = String(numValue); // Keep as percentage for display
        }
      }
      
      item[mappedHeader] = value;
    });
    return item;
  });
  
  return result;
}; 